export const createHeadReconciler = () => {
  let wantedTags = new Map();
  const idByElement = new WeakMap();
  const elementById = new Map();

  const registerElement = (id, el) => {
    idByElement.set(el, id);
    elementById.set(id, el);
  };

  const unregisterElement = el => {
    const id = idByElement.get(el);
    idByElement.delete(el);
    if (id !== undefined) elementById.delete(id);
  };

  const findExistingNode = tag => {
    const marked = elementById.get(tag.id);
    if (marked && document.head.contains(marked)) return marked;
    if (marked) unregisterElement(marked);

    const candidates = document.head.querySelectorAll(tag.matchSelector);
    for (const candidate of candidates) {
      if (!idByElement.has(candidate)) return candidate;
    }

    return null;
  };

  const lastManagedElement = () => {
    const managed = Array.from(document.head.children).filter(child => idByElement.has(child));

    return managed[managed.length - 1] ?? null;
  };

  const insertIntoHead = el => {
    const last = lastManagedElement();

    if (last) {
      last.after(el);
    } else {
      document.head.appendChild(el);
    }
  };

  const upsertTag = tag => {
    let el = findExistingNode(tag);

    if (!el) {
      el = document.createElement(tag.tagName);
      tag.setStaticAttrs(el);
      insertIntoHead(el);
    }

    registerElement(tag.id, el);
    tag.applyValue(el, tag.value);
  };

  // Third-party scripts (widgets, pixels) sometimes rewrite <head>; put our tags back if they get removed.
  const restoreRemovedTags = mutations => {
    mutations.forEach(mutation => {
      mutation.removedNodes.forEach(node => {
        if (node.nodeType !== 1) return;

        const id = idByElement.get(node);
        if (id === undefined) return;

        const tag = wantedTags.get(id);
        if (tag) upsertTag(tag);
      });
    });
  };

  new MutationObserver(restoreRemovedTags).observe(document.head, {
    childList: true,
    subtree: true,
  });

  const applyDesiredHead = ({ title, tags }) => {
    wantedTags = tags;

    if (title) document.title = title;

    Array.from(elementById.entries()).forEach(([id, el]) => {
      if (!tags.has(id)) {
        unregisterElement(el);
        el.remove();
      }
    });

    tags.forEach(upsertTag);
  };

  return { applyDesiredHead };
};
